import React from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Button,
    IconButton,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Grid
} from '@material-ui/core';
import {
    ExpandMore as ExpandMoreIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import useChallengeStore from '../../store/challengeStore';
import QuestionTypeFields from './QuestionTypeFields';
import { useQuestionTypes } from '../../hooks/useQuestionTypes';
import { useSkills } from '../../hooks/useSkills';
import ImageUpload from './ImageUpload';
import UcIndicatorPicker from './UcIndicatorPicker';

import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';

// quill-emoji must be imported as default export
import quillEmoji from 'quill-emoji';

// Register quill-emoji modules
Quill.register('modules/emoji-shortname', quillEmoji.ShortNameEmoji);
Quill.register('modules/emoji-toolbar', quillEmoji.ToolbarEmoji);
Quill.register('formats/emoji', quillEmoji.EmojiBlot);

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3),
        width: '100%',
        margin: 0
    },
    addButton: {
        marginBottom: theme.spacing(3),
        backgroundColor: '#ff9800',
        color: '#fff',
        padding: theme.spacing(1.5, 4),
        fontSize: '1rem',
        fontWeight: 600,
        borderRadius: theme.spacing(1),
        textTransform: 'none',
        boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
        transition: 'all 0.3s ease',
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        },
        '&:hover': {
            backgroundColor: '#e68900',
            boxShadow: '0 6px 16px rgba(255, 152, 0, 0.4)',
            transform: 'translateY(-2px)'
        },
        '&:focus': {
            outline: 'none'
        }
    },
    accordion: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
        border: '1px solid #ff9800',
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0 2px 4px rgba(255, 152, 0, 0.2)',
        '&:before': {
            display: 'none'
        }
    },
    accordionSummary: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    formControl: {
        marginBottom: theme.spacing(2),
        width: '100%'
    },
    dragHandle: {
        cursor: 'grab',
        '&:active': {
            cursor: 'grabbing'
        }
    },
    deleteButton: {
        marginLeft: 'auto'
    },
    quillEditor: {
        '& .ql-container': {
            minHeight: '150px',
            fontSize: '16px',
            fontFamily: theme.typography.fontFamily
        },
        '& .ql-editor': {
            minHeight: '150px'
        },
        '& .ql-toolbar': {
            backgroundColor: '#f5f5f5',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
        }
    },
    quillLabel: {
        marginBottom: theme.spacing(1),
        fontSize: '12px',
        color: 'rgba(0, 0, 0, 0.54)',
        fontWeight: 400
    }
}));

// Quill toolbar configuration
const quillModules = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['link'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['emoji'],
        ['clean']
    ],
    'emoji-toolbar': true,
    'emoji-shortname': true
};

const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link',
    'color', 'background',
    'align',
    'emoji'
];

// Helper function to strip HTML and truncate text
const stripHtmlAndTruncate = (html, maxLength = 50) => {
    if (!html) return 'New Step';
    const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
};

const SortableStep = ({ step, index, onUpdate, onDelete, questionTypes, metaSkills, hardSkills, timerType }) => {
    const classes = useStyles();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: step.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    const handleChange = (field) => (event) => {
        onUpdate(step.id, field, event.target.value);
    };

    const handleQuillChange = (value) => {
        onUpdate(step.id, 'questionField', value);
    };

    const handleQuestionTypeFieldUpdate = (field, value) => {
        onUpdate(step.id, field, value);
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Accordion className={classes.accordion}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={classes.accordionSummary}
                >
                    <Box display="flex" alignItems="center" width="100%">
                        <IconButton
                            size="small"
                            className={classes.dragHandle}
                            {...attributes}
                            {...listeners}
                        >
                            <DragIcon />
                        </IconButton>
                        <Typography>
                            Step {index + 1}: {stripHtmlAndTruncate(step.questionField)}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(step.id);
                            }}
                            className={classes.deleteButton}
                            color="secondary"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box width="100%">
                        <Grid container spacing={2}>
                            {/* Row 1: Question Type */}
                            <Grid item xs={12}>
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel>Question Type *</InputLabel>
                                    <Select
                                        value={step.questionType}
                                        onChange={handleChange('questionType')}
                                        label="Question Type *"
                                    >
                                        <MenuItem value="">Select...</MenuItem>
                                        {questionTypes.map((type) => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Row 2: Question Description */}
                            <Grid item xs={12}>
                                <Typography className={classes.quillLabel}>
                                    Question Description *
                                </Typography>
                                <Box className={classes.quillEditor}>
                                    <ReactQuill
                                        theme="snow"
                                        value={step.questionField || ''}
                                        onChange={handleQuillChange}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder="Enter question description with formatting..."
                                    />
                                </Box>
                            </Grid>

                            {/* Row 3: Image Link/Upload for the question description */}
                            <Grid item xs={12}>
                                <ImageUpload
                                    label="Image Link/Upload for the question description"
                                    value={step.imageLink}
                                    onChange={handleChange('imageLink')}
                                    uniqueId={`step-${step.id}`}
                                />
                            </Grid>

                            {/* Row 3b: Unit of Competence Indicator */}
                            <Grid item xs={12}>
                                <UcIndicatorPicker
                                    value={step.skillDimensionRef}
                                    onChange={(val) => onUpdate(step.id, 'skillDimensionRef', val)}
                                />
                            </Grid>
                        </Grid>

                        {/* Question Type Specific Fields - Moved after Question Description */}
                        <Box mt={2} mb={2}>
                            <QuestionTypeFields
                                step={step}
                                onUpdate={handleQuestionTypeFieldUpdate}
                            />
                        </Box>

                        <Grid container spacing={2}>
                            {/* Row 4: Blocked Step */}
                            <Grid item xs={12} sm={6}>
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel>Blocked Step</InputLabel>
                                    <Select
                                        value={step.isBlockedStep || false}
                                        onChange={handleChange('isBlockedStep')}
                                        label="Blocked Step"
                                    >
                                        <MenuItem value={false}>No</MenuItem>
                                        <MenuItem value={true}>Yes</MenuItem>
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                                    Marks this step as blocked for the user
                                </Typography>
                            </Grid>

                            {/* Row 4b: Evaluation Criteria */}
                            <Grid item xs={12} sm={6}>
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel>Evaluation Criteria</InputLabel>
                                    <Select
                                        value={step.evaluationCriteria || false}
                                        onChange={handleChange('evaluationCriteria')}
                                        label="Evaluation Criteria"
                                    >
                                        <MenuItem value={false}>No</MenuItem>
                                        <MenuItem value={true}>Yes</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Row 5: Step Score (Passed) and Step Score (Failed) */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Step Score (Passed)"
                                    type="number"
                                    value={step.stepScorePassed}
                                    onChange={handleChange('stepScorePassed')}
                                    fullWidth
                                    variant="outlined"
                                    inputProps={{ min: 1, max: 100 }}
                                    helperText="Value from 1 to 100"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Step Score (Failed)"
                                    type="number"
                                    value={step.stepScoreFailed}
                                    fullWidth
                                    variant="outlined"
                                    disabled
                                    helperText="Fixed at 0 for POC"
                                />
                            </Grid>

                            {/* Row 6: Meta Skills and Hard Skills */}
                            <Grid item xs={12} sm={6}>
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel>Meta Skills *</InputLabel>
                                    <Select
                                        value={step.stepMetaSkill || ''}
                                        onChange={handleChange('stepMetaSkill')}
                                        label="Meta Skills *"
                                    >
                                        <MenuItem value="">Select...</MenuItem>
                                        {metaSkills.map((skill) => (
                                            <MenuItem key={skill} value={skill}>
                                                {skill}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                                    Request for New Meta Skill Addition – GAMS Platform
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel>Hard Skills *</InputLabel>
                                    <Select
                                        value={step.stepHardSkill || ''}
                                        onChange={handleChange('stepHardSkill')}
                                        label="Hard Skills *"
                                    >
                                        <MenuItem value="">Select...</MenuItem>
                                        {hardSkills.map((skill) => (
                                            <MenuItem key={skill} value={skill}>
                                                {skill}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                                    You can add new Hard-Skills on admin settings
                                </Typography>
                            </Grid>

                            {/* Row 7: Countdown Time (only if timerEnabled is true) */}
                            {timerType === 'countdown' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Countdown Time (seconds)"
                                        type="number"
                                        value={step.countdownTime}
                                        onChange={handleChange('countdownTime')}
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Time available to complete the step"
                                        helperText="Configure countdown time for this step"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </div>
    );
};

const Page2StepsManagement = () => {
    const classes = useStyles();
    const { steps, addStep, removeStep, updateStep, reorderSteps, timerType } = useChallengeStore();
    console.log('timerType value:', timerType);
    const { isFetching: isQuestionTypeLoading, error: questionTypeError, data: questionTypeData } = useQuestionTypes();
    const { isFetching: isLoadingMetaSkills, error: errorMetaSkills, data: dataMetaSkills } = useSkills('metaSkill');
    const { isFetching: isLoadingHardSkills, error: errorHardSkills, data: dataHardSkills } = useSkills('hardSkill');

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8
            }
        })
    );

    // Map API data to select options format
    // restClient automatically unwraps response.data.data, so we get { questionTypes: [...] } directly
    const questionTypes = React.useMemo(() => {
        console.log('Question Type Data:', questionTypeData);

        if (!questionTypeData?.questionTypes) {
            console.log('No question types found in data');
            return [];
        }

        const types = questionTypeData.questionTypes.map(qt => ({
            value: qt.name,
            label: qt.name.charAt(0).toUpperCase() + qt.name.slice(1).replace(/([A-Z])/g, ' $1').trim()
        }));

        console.log('Mapped question types:', types);
        return types;
    }, [questionTypeData]);

    // Map meta skills from API
    const metaSkills = React.useMemo(() => {
        console.log('Meta Skills Data:', dataMetaSkills);
        if (!dataMetaSkills?.skills) {
            return [];
        }
        return dataMetaSkills.skills.map(skill => skill.name);
    }, [dataMetaSkills]);

    // Map hard skills from API
    const hardSkills = React.useMemo(() => {
        console.log('Hard Skills Data:', dataHardSkills);
        if (!dataHardSkills?.skills) {
            return [];
        }
        return dataHardSkills.skills.map(skill => skill.name);
    }, [dataHardSkills]);

    // Auto-select default values when skills lists load
    React.useEffect(() => {
        if (metaSkills.length === 0 && hardSkills.length === 0) return;
        const currentSteps = useChallengeStore.getState().steps;
        currentSteps.forEach((step) => {
            if (!step.stepMetaSkill && metaSkills.includes('Professional Qualification')) {
                updateStep(step.id, 'stepMetaSkill', 'Professional Qualification');
            }
            if (!step.stepHardSkill && hardSkills.includes('Execution for Qualification')) {
                updateStep(step.id, 'stepHardSkill', 'Execution for Qualification');
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metaSkills, hardSkills]);

    const handleAddStep = () => {
        addStep();
        const newSteps = useChallengeStore.getState().steps;
        const newStep = newSteps[newSteps.length - 1];
        if (newStep) {
            if (metaSkills.includes('Professional Qualification')) {
                updateStep(newStep.id, 'stepMetaSkill', 'Professional Qualification');
            }
            if (hardSkills.includes('Execution for Qualification')) {
                updateStep(newStep.id, 'stepHardSkill', 'Execution for Qualification');
            }
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = steps.findIndex((item) => item.id === active.id);
            const newIndex = steps.findIndex((item) => item.id === over.id);
            const newSteps = arrayMove(steps, oldIndex, newIndex);
            reorderSteps(newSteps);
        }
    };

    // Show loading state
    if (isQuestionTypeLoading || isLoadingMetaSkills || isLoadingHardSkills) {
        return (
            <Paper className={classes.container} elevation={2}>
                <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
                    Challenge Steps Management
                </Typography>
                <Box textAlign="center" py={4}>
                    <Typography color="textSecondary">Loading data...</Typography>
                </Box>
            </Paper>
        );
    }

    // Show error state
    if (questionTypeError || errorMetaSkills || errorHardSkills) {
        const errorMessage = questionTypeError?.message || errorMetaSkills?.message || errorHardSkills?.message;
        return (
            <Paper className={classes.container} elevation={2}>
                <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
                    Challenge Steps Management
                </Typography>
                <Box textAlign="center" py={4}>
                    <Typography color="error">Error loading data: {errorMessage}</Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper className={classes.container} elevation={2}>
            <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
                Challenge Steps Management
            </Typography>

            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddStep}
                className={classes.addButton}
            >
                Add Step
            </Button>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={steps.map(s => s.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {steps.map((step, index) => (
                        <SortableStep
                            key={step.id}
                            step={step}
                            index={index}
                            onUpdate={updateStep}
                            onDelete={removeStep}
                            questionTypes={questionTypes}
                            metaSkills={metaSkills}
                            hardSkills={hardSkills}
                            timerType={timerType}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            {steps.length === 0 && (
                <Box mt={3} textAlign="center">
                    <Typography color="textSecondary">
                        No steps added. Click "Add Step" to start.
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default Page2StepsManagement;
